import { useNavigate, useSearch } from '@tanstack/react-router'
import { z } from 'zod'
import { useCallback } from 'react'
import { parseFacilityKeys, serializeFacilityKeys } from '../search/utils/facilityOptions'
import { parseCategorySlugs, serializeCategorySlugs } from '../search/utils/categoryOptions'

const booleanSchema = z
  .union([z.boolean(), z.enum(['true', 'false'])])
  .transform((v) => v === true || v === 'true')
  .default(false)
  .catch(false)

// Sanitizes the comma-separated facility list to known keys so a mistyped URL
// never reaches the API (unknown keys are a 400 there).
const facilitySchema = z
  .string()
  .optional()
  .transform((v) => serializeFacilityKeys(parseFacilityKeys(v)))
  .catch(undefined)

const categorySchema = z
  .string()
  .optional()
  .transform((v) => serializeCategorySlugs(parseCategorySlugs(v)))
  .catch(undefined)

export const sortOrderValues = ['start', 'price', 'discount'] as const
export const sortDirectionValues = ['asc', 'desc'] as const

export const FILTER_DEFAULTS = {
  region: 'all',
  labelId: undefined,
  beds: 1,
  cribs: 0,
  adult: false,
  pets: false,
  facility: undefined,
  category: undefined,
} as const

export type SortOrder = (typeof sortOrderValues)[number]
export type SortDirection = (typeof sortDirectionValues)[number]

export const locationSearchSchema = z.object({
  fromDate: z.coerce.date().optional().catch(undefined),
  toDate: z.coerce.date().optional().catch(undefined),
  region: z.string().default('all').catch('all'),
  labelId: z.coerce.number().int().optional().catch(undefined),
  beds: z.coerce.number().int().default(1).catch(1),
  cribs: z.coerce.number().int().default(0).catch(0),
  adult: booleanSchema,
  pets: booleanSchema,
  facility: facilitySchema,
  category: categorySchema,
  skip: z.coerce.number().int().default(0).catch(0),
  take: z.coerce.number().int().default(12).catch(12),
  order: z.enum(sortOrderValues).default('start').catch('start'),
  direction: z.enum(sortDirectionValues).default('asc').catch('asc'),
  view: z.enum(['list', 'map']).optional().catch(undefined),
})

export type LocationSearchParams = {
  fromDate?: Date
  toDate?: Date
  region: string
  labelId?: number
  beds: number
  cribs: number
  adult: boolean
  pets: boolean
  /** Comma-separated facility keys (e.g. "sauna,shower"), AND logic on the API. */
  facility?: string
  /** Comma-separated category slugs (e.g. "chata,glamping"), OR logic on the API. */
  category?: string
  skip: number
  take: number
  order: SortOrder
  direction: SortDirection
}

export function useLocationSearchParams() {
  const navigate = useNavigate()
  const searchParams = useSearch({
    strict: false,
  })

  const filters: LocationSearchParams = {
    fromDate: searchParams.fromDate,
    toDate: searchParams.toDate,
    region: searchParams.region ?? 'all',
    labelId: searchParams.labelId,
    beds: searchParams.beds ?? 1,
    cribs: searchParams.cribs ?? 0,
    adult: searchParams.adult ?? false,
    pets: searchParams.pets ?? false,
    facility: searchParams.facility,
    category: searchParams.category,
    skip: searchParams.skip ?? 0,
    take: searchParams.take ?? 12,
    order: searchParams.order ?? 'start',
    direction: searchParams.direction ?? 'asc',
  }

  const setFilters = useCallback(
    (newFilters: LocationSearchParams) => {
      void navigate({
        to: '/ubytovani/hledat',
        // Functional update so the list/map view choice survives filter changes.
        search: (prev: Record<string, unknown>) => ({
          fromDate: newFilters.fromDate,
          toDate: newFilters.toDate,
          region: newFilters.region,
          labelId: newFilters.labelId,
          beds: newFilters.beds,
          cribs: newFilters.cribs,
          adult: newFilters.adult,
          pets: newFilters.pets,
          facility: newFilters.facility,
          category: newFilters.category,
          skip: newFilters.skip,
          take: newFilters.take,
          order: newFilters.order,
          direction: newFilters.direction,
          view: prev.view === 'map' ? ('map' as const) : undefined,
        }),
      })
    },
    [navigate],
  )

  return {
    filters,
    setFilters,
  }
}
